import { Schema } from "@effect/schema";
import { Address } from "./address";
import { PositionType } from "./position";
import { PointsItem } from "./points";
import { WarningConfig } from "./market";
import { AssetKey, DepositMode } from "./market-asset/market-asset";
import { ProtocolKey } from "./protocol";

export type TokenConfig = typeof TokenConfig.Type;
export type TokenConfigEncoded = typeof TokenConfig.Encoded;

export const TokenConfig = Schema.Struct({
  name: Schema.optional(Schema.String),
  symbol: Schema.optional(Schema.String),
}).annotations({ identifier: "TokenConfig" });

export type ChainConfig = typeof ChainConfig.Type;
export type ChainConfigEncoded = typeof ChainConfig.Encoded;

export const ChainConfig = Schema.Struct({
  tokens: Schema.Record({
    key: Schema.String,
    value: Schema.UndefinedOr(TokenConfig),
  }),
}).annotations({ identifier: "ChainConfig" });

export type ConfigAssetPoints = typeof ConfigAssetPoints.Type;
export type ConfigAssetPointsEncoded = typeof ConfigAssetPoints.Encoded;

export const ConfigAssetPoints = Schema.partial(
  Schema.Record({ key: PositionType, value: Schema.Array(PointsItem) })
).annotations({ identifier: "ConfigAssetPoints" });

export type MarketConfig = typeof MarketConfig.Type;
export type MarketConfigEncoded = typeof MarketConfig.Encoded;

export const MarketConfig = Schema.Struct({
  points: Schema.optional(
    Schema.partial(
      Schema.Record({
        key: AssetKey,
        value: ConfigAssetPoints,
      })
    )
  ),
  isDebtAprFixed: Schema.optional(
    Schema.partial(Schema.Record({ key: AssetKey, value: Schema.Boolean }))
  ),
  depositMode: Schema.optional(
    Schema.partial(Schema.Record({ key: AssetKey, value: DepositMode }))
  ),
  curveLpTokenAddress: Schema.optional(Address),
  warningConfig: Schema.optional(WarningConfig),
  hasUnstableOracle: Schema.optional(Schema.Boolean),
  labelAsDeprecated: Schema.optional(Schema.Boolean),
  suppressTvl: Schema.optional(Schema.Boolean),
}).annotations({ identifier: "MarketConfig" });

export type ProtocolConfig = typeof ProtocolConfig.Type;
export type ProtocolConfigEncoded = typeof ProtocolConfig.Encoded;

export const ProtocolConfig = Schema.Struct({
  firstBridgeSymbol: Schema.String,
  secondBridgeSymbol: Schema.NullOr(Schema.String),
  nativeRewards: Schema.NullOr(
    Schema.Struct({
      siloIncentivesControllerAddress: Address,
      tokenAddress: Address,
      symbol: Schema.String,
      decimals: Schema.Number,
      historicalShareTokens: Schema.Record({
        key: Schema.String,
        value: Schema.UndefinedOr(Schema.Array(Address)),
      }),
    })
  ),
  markets: Schema.Record({
    key: Schema.String,
    value: Schema.UndefinedOr(MarketConfig),
  }),
  marketWhitelist: Schema.Set(Address),
  pinnedMarkets: Schema.Array(Address),
}).annotations({ identifier: "ProtocolConfig" });

export type GlobalConfig = typeof GlobalConfig.Type;
export type GlobalConfigEncoded = typeof GlobalConfig.Encoded;

export const GlobalConfig = Schema.Struct({
  revenue: Schema.Struct({
    protocolKey: ProtocolKey,
    marketAddress: Address,
    siloIncentivesControllerAddress: Address,
    targetTokenAddress: Address,
    targetTokenSymbol: Schema.String,
    targetTokenDecimals: Schema.Number,
    shareTokenAddress: Address,
    shareTokenType: PositionType,
    rewardTokenAddress: Address,
    rewardTokenSymbol: Schema.String,
    rewardTokenDecimals: Schema.Number,
  }),
}).annotations({ identifier: "GlobalConfig" });
